import { create } from 'zustand';

export type SidePanelTab = 'plan' | 'workflow' | 'credentials' | 'debug' | 'report';

// Panel width constraints
const DEFAULT_PANEL_WIDTH = 384; // w-96
const MIN_PANEL_WIDTH = 280;     // ~w-72
const MAX_PANEL_WIDTH = 800;     // reasonable max

// Helper to get initial expanded sections from localStorage
const getInitialExpandedSections = (): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem('expandedSections');
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore parse errors
  }
  return {};
};

// Helper to get initial width from localStorage
const getInitialPanelWidth = (): number => {
  const saved = localStorage.getItem('sidePanelWidth');
  if (saved) {
    const parsed = Number(saved);
    if (!isNaN(parsed)) {
      return Math.min(Math.max(parsed, MIN_PANEL_WIDTH), MAX_PANEL_WIDTH);
    }
  }
  return DEFAULT_PANEL_WIDTH;
};

interface UIState {
  // Side panel state
  sidePanelOpen: boolean;
  activeSidePanelTab: SidePanelTab;
  sidePanelWidth: number;

  // Sidebar collapse state
  sidebarCollapsed: boolean;

  // Mobile sidebar drawer state
  mobileSidebarOpen: boolean;

  // Expandable sections state (keyed by section ID)
  expandedSections: Record<string, boolean>;

  // Side panel actions
  toggleSidePanel: () => void;
  openSidePanel: () => void;
  closeSidePanel: () => void;
  setActiveSidePanelTab: (tab: SidePanelTab) => void;
  setSidePanelWidth: (width: number) => void;
  resetSidePanelWidth: () => void;

  // Sidebar actions
  toggleSidebar: () => void;

  // Mobile sidebar actions
  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;

  // Section actions
  toggleSection: (id: string) => void;
  setSection: (id: string, expanded: boolean) => void;
  resetSections: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  // State
  sidePanelOpen: false,
  activeSidePanelTab: 'plan',
  sidePanelWidth: getInitialPanelWidth(),
  sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',
  mobileSidebarOpen: false,
  expandedSections: getInitialExpandedSections(),

  // Side panel actions
  toggleSidePanel: () =>
    set((state) => ({ sidePanelOpen: !state.sidePanelOpen })),

  openSidePanel: () => set({ sidePanelOpen: true }),

  closeSidePanel: () => set({ sidePanelOpen: false }),

  setActiveSidePanelTab: (activeSidePanelTab) => set({ activeSidePanelTab }),

  setSidePanelWidth: (width) =>
    set(() => {
      const clamped = Math.min(Math.max(width, MIN_PANEL_WIDTH), MAX_PANEL_WIDTH);
      localStorage.setItem('sidePanelWidth', String(clamped));
      return { sidePanelWidth: clamped };
    }),

  resetSidePanelWidth: () =>
    set(() => {
      localStorage.setItem('sidePanelWidth', String(DEFAULT_PANEL_WIDTH));
      return { sidePanelWidth: DEFAULT_PANEL_WIDTH };
    }),

  // Sidebar actions
  toggleSidebar: () =>
    set((state) => {
      const newValue = !state.sidebarCollapsed;
      localStorage.setItem('sidebarCollapsed', String(newValue));
      return { sidebarCollapsed: newValue };
    }),

  // Mobile sidebar actions
  openMobileSidebar: () => set({ mobileSidebarOpen: true }),
  closeMobileSidebar: () => set({ mobileSidebarOpen: false }),

  // Section actions
  toggleSection: (id) =>
    set((state) => {
      const newSections = {
        ...state.expandedSections,
        [id]: !state.expandedSections[id],
      };
      localStorage.setItem('expandedSections', JSON.stringify(newSections));
      return { expandedSections: newSections };
    }),

  setSection: (id, expanded) =>
    set((state) => {
      const newSections = {
        ...state.expandedSections,
        [id]: expanded,
      };
      localStorage.setItem('expandedSections', JSON.stringify(newSections));
      return { expandedSections: newSections };
    }),

  resetSections: () => {
    localStorage.removeItem('expandedSections');
    return set({ expandedSections: {} });
  },
}));
